import './anfragen.css';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fmtDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { useKanzlei } from '../context';
import { AREA_LABEL } from './labels';
import { RequestDialog } from './RequestDialog';
import type { OffeneAnfrage, OffeneAnfragenResponse } from './types';

// Seite „Offene Anfragen“ (Seitenleiste): alle angeforderten Unterlagen, die noch offen sind, die ältesten oben.
// Unterlagen zu Mitarbeitern (und zur Krankenkasse) gehören zum Lohn, alles andere zur FiBu. FiBu-Anfragen stehen
// beim Lohn leicht transparent darunter: zur Info, öffnen und hochladen geht trotzdem. Lohn-Anfragen sieht die FiBu
// nicht (Datenschutz). Der Admin sieht alles normal. Eine Anfrage öffnet sich hier im Fenster (?anfrage=<id>).
export const useOffeneAnfragen = () => useApi<OffeneAnfragenResponse>('/api/kanzlei/anfragen/offen', { live: ['anfragen'] });

export function OffeneAnfragen() {
  const { me } = useKanzlei();
  const [params, setParams] = useSearchParams();
  const res = useOffeneAnfragen();
  const open = params.get('anfrage');
  const d = res.data;

  useEffect(() => {
    document.title = 'Offene Anfragen · Elbcons Kanzlei';
  }, []);

  const show = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('anfrage', id);
    else next.delete('anfrage');
    setParams(next, { replace: true });
  };

  const mine = d?.items.filter((r) => r.mine) ?? [];
  const others = d?.items.filter((r) => !r.mine) ?? [];
  const sub = me.user.isAdmin
    ? 'Alle angeforderten Unterlagen aus der App, die noch offen sind'
    : 'Angeforderte Unterlagen Ihrer Mandanten, die noch offen sind';

  return (
    <div className="anfragen">
      <div className="head">
        <div>
          <h1>Offene Anfragen</h1>
          <p>{sub} · Mitarbeiter-Unterlagen beim Lohn, alles andere bei der FiBu</p>
        </div>
      </div>
      {!d ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : (
        <>
          <div className="card">
            <div className="ch">
              <h3>{me.user.isAdmin ? 'Alle offenen Anfragen' : 'Für Sie'}</h3>
              <span>{mine.length} offen</span>
            </div>
            {mine.length ? (
              mine.map((r) => <Row key={r.id} r={r} onOpen={show} />)
            ) : (
              <p className="note">Keine offenen Anfragen. Fordert ein Mandant in der App etwas an, erscheint es hier.</p>
            )}
          </div>
          {others.length > 0 && (
            <div className="card">
              <div className="ch">
                <h3>Zur Info: Bereich der Kollegen</h3>
                <span>{others.length} offen</span>
              </div>
              <p className="note anf-oth-note">
                Diese Anfragen bearbeitet meistens der Kollege. Wenn nötig, können Sie sie öffnen und die Unterlage selbst
                hochladen.
              </p>
              {others.map((r) => (
                <Row key={r.id} r={r} onOpen={show} />
              ))}
            </div>
          )}
        </>
      )}
      {open && <RequestDialog key={open} id={open} onClose={() => show(null)} />}
    </div>
  );
}

// wartet seit … (ganze Tage)
function waiting(createdAt: string): string {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000);
  return days < 1 ? 'seit heute' : days === 1 ? 'seit 1 Tag' : `seit ${days} Tagen`;
}

// „neu“ und „neuer Kommentar“ nur bei meinen Anfragen; beim Kollegen zählt, ob er sie gesehen hat
function Row({ r, onOpen }: { r: OffeneAnfrage; onOpen(id: string): void }) {
  const todo = r.mine && (r.unseen || r.newComment);
  return (
    <div className={`task${r.mine ? '' : ' oth'}`}>
      <span className="av" aria-hidden="true">
        📄
      </span>
      <div className="tx">
        <b>{r.label}</b>
        {r.employee && ` · ${r.employee.name}`} · {r.clientName}{' '}
        <span className={`pill ${r.area === 'lohn' ? 'p-warn' : 'p-info'}`}>
          {AREA_LABEL[r.area]}: {r.who ?? 'offen, bitte zuweisen'}
        </span>
        {r.mine && r.unseen && (
          <>
            {' '}
            <span className="pill p-info">neu</span>
          </>
        )}
        {r.mine && r.newComment && (
          <>
            {' '}
            <span className="pill p-warn">💬 neuer Kommentar</span>
          </>
        )}
        {r.files > 0 && (
          <>
            {' '}
            <span className="pill p-ok">Datei hochgeladen</span>
          </>
        )}
        <small>
          {r.note ? `Wunsch: „${r.note}“ · ` : ''}angefordert am {fmtDate(r.createdAt)}
          {r.createdBy ? ` von ${r.createdBy}` : ''} · wartet {waiting(r.createdAt)}
        </small>
      </div>
      <button type="button" className={`btn sm${todo ? ' dark' : ''}`} onClick={() => onOpen(r.id)}>
        Ansehen
      </button>
    </div>
  );
}
